import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import Modal from "../Common/Modal"
import { toast } from "sonner"
import Button from "../Common/Button"
import { useTranslation } from '../../utils/translations';
import { useAuth } from '../../context/AuthContext';

const { FiChevronDown, FiChevronUp, FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiLoader } = FiIcons;

const FAQSection = ({ isAdmin }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentFaq, setCurrentFaq] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const t = useTranslation(user?.language);

  // Fetch FAQs
  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('faq_items_a1b2c3d4e5')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setFaqs(data || []);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
        toast.error(t('helpCenter.faqSection.failedToLoad', 'Failed to load FAQs. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  // Toggle FAQ expansion
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Handle edit
  const handleEdit = (faq) => {
    setCurrentFaq(faq);
    setIsEditing(true);
    setIsCreating(false);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm(t('helpCenter.faqSection.deleteConfirm', 'Are you sure you want to delete this FAQ?'))) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('faq_items_a1b2c3d4e5')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      setFaqs(faqs.filter(faq => faq.id !== id));
      toast.success(t('helpCenter.faqSection.deleteSuccess', 'FAQ deleted successfully.'));
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      toast.error(t('helpCenter.faqSection.deleteFailed', 'Failed to delete FAQ. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Handle save/create
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!currentFaq.title.trim() || !currentFaq.content.trim()) {
      toast.error(t('helpCenter.faqSection.requiredFields', 'Title and content are required.'));
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        // Update existing FAQ
        const { error } = await supabase
          .from('faq_items_a1b2c3d4e5')
          .update({
            title: currentFaq.title,
            content: currentFaq.content,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentFaq.id);

        if (error) throw error;
        
        // Update local state
        setFaqs(faqs.map(faq => 
          faq.id === currentFaq.id ? { ...faq, ...currentFaq, updated_at: new Date().toISOString() } : faq
        ));
        
        toast.success(t('helpCenter.faqSection.updateSuccess', 'FAQ updated successfully.'));
      } else {
        // Create new FAQ
        const { data, error } = await supabase
          .from('faq_items_a1b2c3d4e5')
          .insert([{
            title: currentFaq.title,
            content: currentFaq.content
          }])
          .select();

        if (error) throw error;
        
        // Update local state with the new FAQ
        if (data && data.length > 0) {
          setFaqs([data[0], ...faqs]);
        }

        toast.success(t('helpCenter.faqSection.createSuccess', 'FAQ created successfully.'));
      }

      // Reset form and state
      setCurrentFaq({ title: '', content: '' });
      setIsEditing(false);
      setIsCreating(false);      
    } catch (err) {
      console.error('Error saving FAQ:', err);
      toast.error(t('helpCenter.faqSection.saveFailed', 'Failed to save FAQ. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing/creating
  const handleCancel = () => {
    setCurrentFaq({ title: '', content: '' });
    setIsEditing(false);
    setIsCreating(false);
  };

  if (loading && faqs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('helpCenter.faqSection.title', 'Frequently Asked Questions')}</h2>
        
        {isAdmin && (
          <Button
            onClick={() => {
              setIsCreating(true);
              setCurrentFaq({ title: '', content: '' });
            }}
            icon={FiPlus}
            responsive
          >
            {t('helpCenter.faqSection.addFaq', 'Add FAQ')}
          </Button>
        )}
      </div>

      {/* Edit/Create Form */}
      <Modal isOpen={isEditing || isCreating} title={isEditing ? t('helpCenter.faqSection.editFaqTitle', 'Edit FAQ') : t('helpCenter.faqSection.addFaqTitle', 'Add New FAQ')} onClose={handleCancel}>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="faq-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.faqSection.questionLabel', 'Question')}
            </label>
            <input
              id="faq-title"
              type="text"
              value={currentFaq.title}
              onChange={(e) => setCurrentFaq({ ...currentFaq, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.faqSection.questionPlaceholder', 'Enter the question')}
            />
          </div>

          <div>
            <label htmlFor="faq-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.faqSection.answerLabel', 'Answer')}
            </label>
            <textarea
              id="faq-content"
              value={currentFaq.content}
              onChange={(e) => setCurrentFaq({ ...currentFaq, content: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.faqSection.answerPlaceholder', 'Enter the answer')}
              rows={6}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('helpCenter.faqSection.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              loading={saving}
              icon={FiSave}
            >
              {isEditing ? t('helpCenter.faqSection.updateFaq', 'Update FAQ') : t('helpCenter.faqSection.saveFaq', 'Save FAQ')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* FAQs List */}
      <div className="space-y-4">
        {faqs.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {t('helpCenter.faqSection.noFaqs', 'No FAQs found.')} {isAdmin ? t('helpCenter.faqSection.addFaqPrompt', 'Click the "Add FAQ" button to create one.') : ''}
            </p>
          </div>
        ) : (
          faqs.map((faq) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div 
                className="flex justify-between items-center p-5 cursor-pointer"
                onClick={() => toggleExpand(faq.id)}
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white pr-8">
                  {faq.title}
                </h3>
                <div className="flex flex-none items-center space-x-">
                  {isAdmin && (
                    <div className="flex lg:space-x-2 mr-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(faq);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(faq.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <SafeIcon 
                    icon={expandedId === faq.id ? FiChevronUp : FiChevronDown} 
                    className="w-5 h-5 text-gray-400"
                  />
                </div>
              </div>
              
              {expandedId === faq.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-5 pb-5"
                >
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-2 text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                    {faq.content}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default FAQSection;